import { useCreateDialogForm, useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { useCreateDraft } from 'components/create-dialog/useCreateDraft';
import { DraftSaveMode } from 'components/drafts/DraftSaveMode';
import ActionHeader from 'components/forms/ActionHeader';
import FormWrapper from 'components/forms/form-components/FormWrapper';
import Loader from 'components/Loader';
import Section from 'components/section-menu/Section';
import SectionLayout from 'components/section-menu/SectionLayout';
import { useRef } from 'react';
import { Control, FieldValues, FormProvider } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'store';
import { useAddContractMutation, useUpdateContractMutation } from 'store/api/contracts';
import { ContractForm } from 'types/contracts';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { useGoBack, useLeaveForm, useReturnToLocation } from 'utils/useNavigateWithReturnState';

import CancelButton from 'components/buttons/CancelButton';
import DeleteButton from 'components/buttons/DeleteButton';
import PermissionButton from 'components/buttons/PermissionButton';
import SaveButton from 'components/buttons/SaveButton';
import CustomFields from 'components/forms/inputs/custom-fields/CustomFields';
import ConfirmNavigation from 'components/navigation/ConfirmNavigation.tsx';
import { HighlightedFieldsProvider, SectionActionsProvider } from 'components/section-menu/SectionActionsContext';
import useFocusedEditMode from 'hooks/useFocusedEditMode';
import { contractFormToRequest } from './contractFormMapping';
import {
    BeneficialOwnersFields,
    BillingFields,
    DatesFields,
    FinancialFields,
    GeneralFields,
    NotesFields,
} from './form-sections/ContractFields';
import { ContractFormProps, useContractForm } from './form-sections/useContractForm';
import { useConfirmDeleteContract } from './useConfirmDeleteContract';

const ContractPageForm = (props: ContractFormProps): JSX.Element => {
    const { type = 'add', initialData, refetch, isDeletable } = props;
    useFocusedEditMode('contracts');
    const { id: contractId } = useCreateRoute().params;
    const form = useContractForm(props);
    const { methods, mapCustomFieldsToDto, isAmlOrUbo, isRepresentation, isCooperation, filteredCustomFields } = form;
    useCreateDialogForm(methods);
    const { handleSubmit, control, reset } = methods;
    const toRequest = (formData: ContractForm) =>
        contractFormToRequest(formData, mapCustomFieldsToDto(formData as unknown as Record<string, unknown>));
    // A new contract gets a price list of its own, whatever list the form was started from.
    const toCreateRequest = (formData: ContractForm) => ({ ...toRequest(formData), pricelistId: null });
    const draft = useCreateDraft({ record: 'contract', methods, toPayload: toCreateRequest });

    const { buttonsDisabled } = useSelector((state) => state.buttons);
    const returnTo = useReturnToLocation(contractId ? `/contract/${contractId}` : '/contracts');
    const goBack = useGoBack(returnTo);
    const leaveForm = useLeaveForm();

    const [addContract] = useAddContractMutation();
    const [updateContract] = useUpdateContractMutation();

    const bypassNavigationBlockerRef = useRef(false);

    const onSubmit = async (formData: ContractForm, action: 'create' | 'update'): Promise<void> => {
        try {
            if (action === 'create') {
                const data = toCreateRequest(formData);
                // A draft is created by completing it: the contract keeps the draft's id.
                const created: Promise<{ id: number }> =
                    draft.draftId === null ? addContract(data).unwrap() : draft.complete(data);
                await created
                    .then((resp) => {
                        bypassNavigationBlockerRef.current = true;
                        reset({}, { keepValues: true });
                        leaveForm(`/contract/${resp.id}`);
                    })
                    .catch(console.error);
            } else if (action === 'update' && contractId && !isNaN(Number(contractId))) {
                await updateContract({
                    ...toRequest(formData),
                    id: contractId,
                })
                    .unwrap()
                    .then(() => {
                        bypassNavigationBlockerRef.current = true;
                        reset({}, { keepValues: true });
                        if (refetch) refetch();
                        leaveForm(returnTo);
                    })
                    .catch(console.error);
            }
        } catch (e) {
            console.error('onSubmit error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    const submitCreateContact = () => {
        handleSubmit((data) => onSubmit(data, 'create'))();
    };

    const submitUpdateContact = () => {
        handleSubmit((data) => onSubmit(data, 'update'))();
    };

    const handleDeleteContract = useConfirmDeleteContract(contractId, () => {
        bypassNavigationBlockerRef.current = true;
        reset({}, { keepValues: true });
    });

    if (form.isLoading) {
        return <Loader />;
    }

    // Show inital name if is edit form. Dont do dynamically to prevent unneeded rerenders
    const getFormTitle = () => {
        switch (type) {
            case 'edit':
                return initialData?.contractName;
            default:
                return t('contracts.add-new-contract');
        }
    };

    const getFormHeader = () => {
        const actions: JSX.Element[] = [<CancelButton key="cancel" onClick={goBack} />];

        if (type === 'add') {
            if (draft.button) {
                actions.push(draft.button);
            }
            actions.push(
                <SaveButton
                    key="addSave"
                    permissions={['CREATE.CONTRACTS']}
                    onClick={submitCreateContact}
                    disabled={buttonsDisabled}
                />
            );
        } else if (type === 'edit' && contractId) {
            actions.push(
                <DeleteButton
                    key="editDelete"
                    hasAuthority={isDeletable}
                    onClick={handleDeleteContract}
                    disabled={buttonsDisabled}
                />,
                <PermissionButton
                    key="editSave"
                    permissions={['UPDATE.CONTRACTS.PARTY_ACCESS']}
                    color="secondary"
                    variant="contained"
                    size="small"
                    onClick={submitUpdateContact}
                    disabled={buttonsDisabled}
                >
                    <FormattedMessage id="save" />
                </PermissionButton>
            );
        }

        return <ActionHeader title={getFormTitle()} actions={actions} onBack={goBack} draft={draft.detail} />;
    };

    return (
        <SectionActionsProvider>
            <HighlightedFieldsProvider>
                <FormProvider {...methods}>
                    <FormWrapper>
                        {getFormHeader()}
                        <DraftSaveMode mode={draft.mode}>
                            <SectionLayout>
                                <Section title="contracts.general" collapsible>
                                    <GeneralFields form={form} />
                                </Section>
                                {isAmlOrUbo ? (
                                    <Section title="contracts.ultimate-beneficial-owners" collapsible>
                                        <BeneficialOwnersFields form={form} />
                                    </Section>
                                ) : null}

                                {!isAmlOrUbo && !isRepresentation && !isCooperation ? (
                                    <Section title="contracts.financial" collapsible>
                                        <FinancialFields form={form} />
                                    </Section>
                                ) : null}

                                {!isAmlOrUbo && !isRepresentation && !isCooperation ? (
                                    <Section title="contracts.billing.title" collapsible>
                                        <BillingFields form={form} />
                                    </Section>
                                ) : null}

                                <Section title="contracts.dates" collapsible>
                                    <DatesFields form={form} />
                                </Section>

                                <Section title="contracts.notes" collapsible>
                                    <NotesFields form={form} />
                                </Section>

                                {filteredCustomFields?.length > 0 ? (
                                    <CustomFields
                                        control={control as unknown as Control<FieldValues>}
                                        fields={filteredCustomFields}
                                    />
                                ) : null}
                            </SectionLayout>
                        </DraftSaveMode>
                    </FormWrapper>
                    <ConfirmNavigation
                        control={control}
                        bypassRef={bypassNavigationBlockerRef}
                        draft={type === 'add' ? draft.leave : null}
                    />
                </FormProvider>
            </HighlightedFieldsProvider>
        </SectionActionsProvider>
    );
};

export default ContractPageForm;
